import { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
} from "firebase/firestore";
import {
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { db } from "../firebase";

function AdminReports() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const reportType =
    searchParams.get("type") || "leads";

  const [employees, setEmployees] = useState([]);
  const [leads, setLeads] = useState([]);
  const [attendanceLogs, setAttendanceLogs] =
    useState([]);
  const [loading, setLoading] = useState(true);

  const [searchText, setSearchText] =
    useState("");

  const [employeeFilter, setEmployeeFilter] =
    useState("All");

  const [fromDate, setFromDate] =
    useState("");

  const [toDate, setToDate] =
    useState("");

  const [statusFilter, setStatusFilter] =
    useState("All");

  const [
    customerColumnFilter,
    setCustomerColumnFilter,
  ] = useState("");

  const [
    phoneColumnFilter,
    setPhoneColumnFilter,
  ] = useState("");

  const [
    employeeColumnFilter,
    setEmployeeColumnFilter,
  ] = useState("All");

  const [
    statusColumnFilter,
    setStatusColumnFilter,
  ] = useState("All");

  const [
    remarkColumnFilter,
    setRemarkColumnFilter,
  ] = useState("");

  const todayDate = new Date()
    .toISOString()
    .split("T")[0];

  const reportTitles = {
    employees: "Total Employees",
    working: "Currently Working",
    break: "Employees On Break",
    leads: "Total Leads",
    calls: "Calls Done",
    "pending-leads": "Pending Leads",
    callbacks: "Pending Callbacks",
    converted: "Converted Leads",
  };

  useEffect(() => {
    const loadReportData = async () => {
      try {
        setLoading(true);

        const [
          employeeSnapshot,
          leadSnapshot,
        ] = await Promise.all([
          getDocs(
            collection(db, "employees")
          ),
          getDocs(
            collection(db, "leads")
          ),
        ]);

        setEmployees(
          employeeSnapshot.docs.map(
            (item) => ({
              id: item.id,
              ...item.data(),
            })
          )
        );

        setLeads(
          leadSnapshot.docs.map(
            (item) => ({
              id: item.id,
              ...item.data(),
            })
          )
        );

        try {
          const attendanceSnapshot =
            await getDocs(
              collection(
                db,
                "attendanceLogs"
              )
            );

          setAttendanceLogs(
            attendanceSnapshot.docs.map(
              (item) => ({
                id: item.id,
                ...item.data(),
              })
            )
          );
        } catch (attendanceError) {
          console.warn(
            "Attendance history unavailable:",
            attendanceError
          );

          setAttendanceLogs([]);
        }
      } catch (error) {
        console.error(
          "Admin report loading error:",
          error
        );
      } finally {
        setLoading(false);
      }
    };

    loadReportData();
  }, []);

  const toDateObject = (value) => {
    if (!value) {
      return null;
    }

    try {
      if (
        typeof value.toDate ===
        "function"
      ) {
        return value.toDate();
      }

      const date =
        new Date(value);

      if (
        Number.isNaN(
          date.getTime()
        )
      ) {
        return null;
      }

      return date;
    } catch (error) {
      return null;
    }
  };

  const formatDate = (value) => {
    const date =
      toDateObject(value);

    if (!date) {
      return "-";
    }

    return date.toLocaleString(
      "en-IN",
      {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }
    );
  };

  const formatTimeOnly = (value) => {
    const date =
      toDateObject(value);

    if (!date) {
      return "-";
    }

    return date.toLocaleTimeString(
      "en-IN",
      {
        hour: "2-digit",
        minute: "2-digit",
      }
    );
  };

  const formatDuration = (
    secondsValue
  ) => {
    const totalSeconds =
      Math.max(
        0,
        Math.floor(
          Number(secondsValue) || 0
        )
      );

    const hours =
      Math.floor(
        totalSeconds / 3600
      );

    const minutes =
      Math.floor(
        (totalSeconds % 3600) /
          60
      );

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }

    return `${minutes}m`;
  };

  const getDateKey = (value) => {
    const date =
      toDateObject(value);

    if (!date) {
      return "";
    }

    const year =
      date.getFullYear();

    const month =
      String(
        date.getMonth() + 1
      ).padStart(2, "0");

    const day =
      String(
        date.getDate()
      ).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const isWithinDateFilter = (
    value
  ) => {
    if (
      !fromDate &&
      !toDate
    ) {
      return true;
    }

    const dateValue =
      typeof value ===
        "string" &&
      /^\d{4}-\d{2}-\d{2}$/.test(
        value
      )
        ? value
        : getDateKey(value);

    if (!dateValue) {
      return false;
    }

    if (
      fromDate &&
      dateValue < fromDate
    ) {
      return false;
    }

    if (
      toDate &&
      dateValue > toDate
    ) {
      return false;
    }

    return true;
  };

  const employeeRows =
    useMemo(() => {
      let list =
        [...employees];

      if (
        reportType ===
        "working"
      ) {
        list =
          list.filter(
            (employee) =>
              employee.workStatus ===
              "Working"
          );
      }

      if (
        reportType ===
        "break"
      ) {
        list =
          list.filter(
            (employee) =>
              employee.workStatus ===
              "On Break"
          );
      }

      if (
        employeeFilter !==
        "All"
      ) {
        list =
          list.filter(
            (employee) =>
              employee.id ===
              employeeFilter
          );
      }

      const search =
        searchText
          .trim()
          .toLowerCase();

      if (search) {
        list =
          list.filter(
            (employee) =>
              (
                employee.name ||
                ""
              )
                .toLowerCase()
                .includes(search) ||
              (
                employee.email ||
                ""
              )
                .toLowerCase()
                .includes(search)
          );
      }

      return list;
    }, [
      employees,
      reportType,
      employeeFilter,
      searchText,
    ]);

  const attendanceRows =
    useMemo(() => {
      let list =
        [...attendanceLogs];

      if (
        employeeFilter !==
        "All"
      ) {
        list =
          list.filter(
            (log) =>
              log.employeeId ===
              employeeFilter
          );
      }

      if (
        fromDate ||
        toDate
      ) {
        list =
          list.filter(
            (log) =>
              isWithinDateFilter(
                log.date
              )
          );
      }

      return list;
    }, [
      attendanceLogs,
      employeeFilter,
      fromDate,
      toDate,
    ]);

  const leadRows =
    useMemo(() => {
      let list =
        [...leads];

      if (
        reportType ===
        "pending-leads"
      ) {
        list =
          list.filter(
            (lead) =>
              lead.status !==
                "Converted" &&
              lead.status !==
                "Prospect" &&
              lead.status !==
                "Not Interested"
          );
      }

      if (
        reportType ===
        "callbacks"
      ) {
        list =
          list.filter(
            (lead) =>
              lead.callbackStatus ===
              "Pending"
          );
      }

      if (
        reportType ===
        "converted"
      ) {
        list =
          list.filter(
            (lead) =>
              lead.status ===
              "Converted"
          );
      }

      if (
        reportType ===
          "calls" &&
        (fromDate ||
          toDate)
      ) {
        list =
          list.filter(
            (lead) =>
              isWithinDateFilter(
                lead.lastContactedAt
              )
          );
      }

      if (
        reportType ===
          "leads" &&
        (fromDate ||
          toDate)
      ) {
        list =
          list.filter(
            (lead) =>
              isWithinDateFilter(
                lead.createdAt
              )
          );
      }

      if (
        reportType ===
          "callbacks" &&
        (fromDate ||
          toDate)
      ) {
        list =
          list.filter(
            (lead) =>
              isWithinDateFilter(
                lead.callbackDate
              )
          );
      }

      if (
        employeeFilter !==
        "All"
      ) {
        list =
          list.filter(
            (lead) =>
              lead.assignedEmployeeId ===
              employeeFilter
          );
      }

      if (
        statusFilter !==
        "All"
      ) {
        list =
          list.filter(
            (lead) =>
              (
                lead.status ||
                "Pending"
              ) ===
              statusFilter
          );
      }

      const search =
        searchText
          .trim()
          .toLowerCase();

      if (search) {
        list =
          list.filter(
            (lead) =>
              (
                lead.customerName ||
                lead.name ||
                ""
              )
                .toLowerCase()
                .includes(search) ||
              String(
                lead.phone || ""
              ).includes(search) ||
              (
                lead.assignedEmployeeName ||
                ""
              )
                .toLowerCase()
                .includes(search)
          );
      }

      if (
        customerColumnFilter
          .trim()
      ) {
        const value =
          customerColumnFilter
            .trim()
            .toLowerCase();

        list =
          list.filter(
            (lead) =>
              (
                lead.customerName ||
                lead.name ||
                ""
              )
                .toLowerCase()
                .includes(value)
          );
      }

      if (
        phoneColumnFilter
          .trim()
      ) {
        const value =
          phoneColumnFilter
            .replace(
              /\s+/g,
              ""
            )
            .toLowerCase();

        list =
          list.filter(
            (lead) =>
              String(
                lead.phone ||
                  ""
              )
                .replace(
                  /\s+/g,
                  ""
                )
                .toLowerCase()
                .includes(value)
          );
      }

      if (
        employeeColumnFilter !==
        "All"
      ) {
        list =
          list.filter(
            (lead) =>
              lead.assignedEmployeeId ===
              employeeColumnFilter
          );
      }

      if (
        statusColumnFilter !==
        "All"
      ) {
        list =
          list.filter(
            (lead) =>
              (
                lead.status ||
                "Pending"
              ) ===
              statusColumnFilter
          );
      }

      if (
        remarkColumnFilter
          .trim()
      ) {
        const value =
          remarkColumnFilter
            .trim()
            .toLowerCase();

        list =
          list.filter(
            (lead) =>
              (
                lead.lastRemark ||
                ""
              )
                .toLowerCase()
                .includes(value)
          );
      }

      return list;
    }, [
      leads,
      reportType,
      employeeFilter,
      statusFilter,
      searchText,
      fromDate,
      toDate,
      customerColumnFilter,
      phoneColumnFilter,
      employeeColumnFilter,
      statusColumnFilter,
      remarkColumnFilter,
    ]);

  const showEmployeeTable =
    [
      "employees",
      "working",
      "break",
    ].includes(
      reportType
    );

  const setQuickDate = (
    type
  ) => {
    const now =
      new Date();

    if (
      type === "today"
    ) {
      const value =
        getDateKey(now);

      setFromDate(value);
      setToDate(value);
      return;
    }

    if (
      type ===
      "yesterday"
    ) {
      const date =
        new Date(now);

      date.setDate(
        date.getDate() - 1
      );

      const value =
        getDateKey(date);

      setFromDate(value);
      setToDate(value);
      return;
    }

    if (
      type === "week"
    ) {
      const start =
        new Date(now);

      start.setDate(
        now.getDate() -
          now.getDay()
      );

      setFromDate(
        getDateKey(start)
      );

      setToDate(
        getDateKey(now)
      );

      return;
    }

    if (
      type === "month"
    ) {
      const start =
        new Date(
          now.getFullYear(),
          now.getMonth(),
          1
        );

      setFromDate(
        getDateKey(start)
      );

      setToDate(
        getDateKey(now)
      );
    }
  };

  const clearAllFilters =
    () => {
      setFromDate("");
      setToDate("");

      setEmployeeFilter(
        "All"
      );

      setStatusFilter(
        "All"
      );

      setSearchText("");

      setCustomerColumnFilter(
        ""
      );

      setPhoneColumnFilter(
        ""
      );

      setEmployeeColumnFilter(
        "All"
      );

      setStatusColumnFilter(
        "All"
      );

      setRemarkColumnFilter(
        ""
      );
    };

  const downloadCsv =
    () => {
      const rows =
        showEmployeeTable
          ? [
              [
                "Employee",
                "Email",
                "Status",
                "Latest Check In",
                "Latest Check Out",
              ],

              ...employeeRows.map(
                (employee) => [
                  employee.name ||
                    "",
                  employee.email ||
                    "",
                  employee.workStatus ||
                    "",
                  formatDate(
                    employee.workStartedAt
                  ),
                  formatDate(
                    employee.workEndedAt
                  ),
                ]
              ),
            ]
          : [
              [
                "Customer",
                "Phone",
                "Employee",
                "Status",
                "Latest Remark",
              ],

              ...leadRows.map(
                (lead) => [
                  lead.customerName ||
                    lead.name ||
                    "",
                  lead.phone ||
                    "",
                  lead.assignedEmployeeName ||
                    "",
                  lead.status ||
                    "",
                  lead.lastRemark ||
                    "",
                ]
              ),
            ];

      const csv =
        rows
          .map(
            (row) =>
              row
                .map(
                  (value) =>
                    `"${String(
                      value ?? ""
                    ).replace(
                      /"/g,
                      '""'
                    )}"`
                )
                .join(",")
          )
          .join("\n");

      const blob =
        new Blob(
          [csv],
          {
            type:
              "text/csv;charset=utf-8;",
          }
        );

      const url =
        URL.createObjectURL(
          blob
        );

      const link =
        document.createElement(
          "a"
        );

      link.href = url;

      link.download =
        `trading-wealth-${reportType}-${todayDate}.csv`;

      document.body.appendChild(
        link
      );

      link.click();

      document.body.removeChild(
        link
      );

      URL.revokeObjectURL(
        url
      );
    };

  const columnInputStyle = {
    width: "100%",
    minWidth: "90px",
    height: "32px",
    padding: "4px 8px",
    border:
      "1px solid #bae6fd",
    borderRadius: "7px",
    background:
      "#ffffff",
    fontSize: "11px",
    outline: "none",
    boxSizing:
      "border-box",
  };

  return (
    <div className="admin-dashboard-page">
      <header className="dashboard-header">
        <div>
          <h1>
            Trading Wealth CRM
          </h1>

          <p>
            Admin Reports
          </p>
        </div>

        <button
          type="button"
          className="logout-button"
          onClick={() =>
            navigate(
              "/admin-dashboard"
            )
          }
        >
          Back to Dashboard
        </button>
      </header>

      <main className="dashboard-main">
        <section className="dashboard-welcome">
          <p className="dashboard-label">
            ADMIN REPORT
          </p>

          <h2>
            {reportTitles[
              reportType
            ] || "CRM Report"}
          </h2>

          <p>
            Filter, inspect and download complete CRM records.
          </p>
        </section>

        <section className="employee-form-card">
          <div className="employee-form-grid">
            <div className="form-group">
              <label>
                Search
              </label>

              <input
                type="text"
                value={
                  searchText
                }
                onChange={(
                  event
                ) =>
                  setSearchText(
                    event.target.value
                  )
                }
                placeholder={
                  showEmployeeTable
                    ? "Employee name or email"
                    : "Customer, phone or employee"
                }
              />
            </div>

            <div className="form-group">
              <label>
                Employee
              </label>

              <select
                value={
                  employeeFilter
                }
                onChange={(
                  event
                ) =>
                  setEmployeeFilter(
                    event.target.value
                  )
                }
              >
                <option value="All">
                  All Employees
                </option>

                {employees.map(
                  (employee) => (
                    <option
                      key={
                        employee.id
                      }
                      value={
                        employee.id
                      }
                    >
                      {employee.name}
                    </option>
                  )
                )}
              </select>
            </div>

            <div className="form-group">
              <label>
                From Date
              </label>

              <input
                type="date"
                value={
                  fromDate
                }
                onChange={(
                  event
                ) =>
                  setFromDate(
                    event.target.value
                  )
                }
              />
            </div>

            <div className="form-group">
              <label>
                To Date
              </label>

              <input
                type="date"
                value={
                  toDate
                }
                onChange={(
                  event
                ) =>
                  setToDate(
                    event.target.value
                  )
                }
              />
            </div>

            {!showEmployeeTable && (
              <div className="form-group">
                <label>
                  Status
                </label>

                <select
                  value={
                    statusFilter
                  }
                  onChange={(
                    event
                  ) =>
                    setStatusFilter(
                      event.target.value
                    )
                  }
                >
                  <option value="All">
                    All Statuses
                  </option>
                  <option value="Pending">
                    Pending
                  </option>
                  <option value="Interested">
                    Interested
                  </option>
                  <option value="Not Interested">
                    Not Interested
                  </option>
                  <option value="Busy">
                    Busy
                  </option>
                  <option value="Ringing">
                    Ringing
                  </option>
                  <option value="Switched Off">
                    Switched Off
                  </option>
                  <option value="Out of Coverage">
                    Out of Coverage
                  </option>
                  <option value="Not Answered">
                    Not Answered
                  </option>
                  <option value="Prospect">
                    Prospect
                  </option>
                  <option value="Converted">
                    Converted
                  </option>
                </select>
              </div>
            )}
          </div>

          <div
            style={{
              display: "flex",
              gap: "8px",
              flexWrap: "wrap",
              marginTop: "15px",
            }}
          >
            <button
              type="button"
              className="table-button"
              onClick={() =>
                setQuickDate(
                  "today"
                )
              }
            >
              Today
            </button>

            <button
              type="button"
              className="table-button"
              onClick={() =>
                setQuickDate(
                  "yesterday"
                )
              }
            >
              Yesterday
            </button>

            <button
              type="button"
              className="table-button"
              onClick={() =>
                setQuickDate(
                  "week"
                )
              }
            >
              This Week
            </button>

            <button
              type="button"
              className="table-button"
              onClick={() =>
                setQuickDate(
                  "month"
                )
              }
            >
              This Month
            </button>

            <button
              type="button"
              className="secondary-button"
              onClick={
                clearAllFilters
              }
            >
              Clear Filters
            </button>

            <button
              type="button"
              className="admin-login-button employee-save-button"
              onClick={
                downloadCsv
              }
            >
              Download CSV
            </button>
          </div>
        </section>

        {loading ? (
          <div className="employee-empty-state">
            Loading report...
          </div>
        ) : showEmployeeTable ? (
          <section className="dashboard-section">
            <div className="section-heading">
              <div>
                <p className="dashboard-label">
                  EMPLOYEE RECORDS
                </p>

                <h2>
                  {
                    employeeRows.length
                  }{" "}
                  Employees
                </h2>

                <p
                  style={{
                    marginTop:
                      "5px",
                    color:
                      "#64748b",
                  }}
                >
                  Click an employee name to view complete attendance history.
                </p>
              </div>
            </div>

            <div className="employee-table-wrapper">
              <table className="employee-table">
                <thead>
                  <tr>
                    <th>
                      Employee
                    </th>

                    <th>
                      Status
                    </th>

                    <th>
                      Latest Check In
                    </th>

                    <th>
                      Latest Check Out
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {employeeRows.length ===
                  0 ? (
                    <tr>
                      <td colSpan="4">
                        No employees found.
                      </td>
                    </tr>
                  ) : (
                    employeeRows.map(
                      (employee) => (
                        <tr
                          key={
                            employee.id
                          }
                        >
                          <td>
                            <button
                              type="button"
                              onClick={() =>
                                navigate(
                                  `/admin-employee-history/${employee.id}`
                                )
                              }
                              style={{
                                border:
                                  "none",
                                background:
                                  "transparent",
                                padding: 0,
                                color:
                                  "#0284c7",
                                fontWeight:
                                  "800",
                                cursor:
                                  "pointer",
                                textAlign:
                                  "left",
                              }}
                            >
                              {
                                employee.name
                              }
                            </button>

                            <small
                              style={{
                                display:
                                  "block",
                              }}
                            >
                              {
                                employee.email
                              }
                            </small>
                          </td>

                          <td>
                            {employee.workStatus ||
                              "Not Started"}
                          </td>

                          <td>
                            {formatDate(
                              employee.workStartedAt
                            )}
                          </td>

                          <td>
                            {formatDate(
                              employee.workEndedAt
                            )}
                          </td>
                        </tr>
                      )
                    )
                  )}
                </tbody>
              </table>
            </div>
          </section>
        ) : (
          <section className="dashboard-section">
            <div className="section-heading">
              <div>
                <p className="dashboard-label">
                  CUSTOMER RECORDS
                </p>

                <h2>
                  {
                    leadRows.length
                  }{" "}
                  Records
                </h2>
              </div>
            </div>

            <div className="employee-table-wrapper">
              <table className="employee-table">
                <thead>
                  <tr>
                    <th>
                      Customer
                    </th>
                    <th>
                      Phone
                    </th>
                    <th>
                      Employee
                    </th>
                    <th>
                      Status
                    </th>
                    <th>
                      Latest Remark
                    </th>
                  </tr>

                  <tr>
                    <th>
                      <input
                        type="text"
                        value={
                          customerColumnFilter
                        }
                        onChange={(
                          event
                        ) =>
                          setCustomerColumnFilter(
                            event.target.value
                          )
                        }
                        placeholder="Filter name"
                        style={
                          columnInputStyle
                        }
                      />
                    </th>

                    <th>
                      <input
                        type="text"
                        value={
                          phoneColumnFilter
                        }
                        onChange={(
                          event
                        ) =>
                          setPhoneColumnFilter(
                            event.target.value
                          )
                        }
                        placeholder="Filter phone"
                        style={
                          columnInputStyle
                        }
                      />
                    </th>

                    <th>
                      <select
                        value={
                          employeeColumnFilter
                        }
                        onChange={(
                          event
                        ) =>
                          setEmployeeColumnFilter(
                            event.target.value
                          )
                        }
                        style={
                          columnInputStyle
                        }
                      >
                        <option value="All">
                          All
                        </option>

                        {employees.map(
                          (employee) => (
                            <option
                              key={
                                employee.id
                              }
                              value={
                                employee.id
                              }
                            >
                              {
                                employee.name
                              }
                            </option>
                          )
                        )}
                      </select>
                    </th>

                    <th>
                      <select
                        value={
                          statusColumnFilter
                        }
                        onChange={(
                          event
                        ) =>
                          setStatusColumnFilter(
                            event.target.value
                          )
                        }
                        style={
                          columnInputStyle
                        }
                      >
                        <option value="All">
                          All
                        </option>
                        <option value="Pending">
                          Pending
                        </option>
                        <option value="Interested">
                          Interested
                        </option>
                        <option value="Not Interested">
                          Not Interested
                        </option>
                        <option value="Busy">
                          Busy
                        </option>
                        <option value="Ringing">
                          Ringing
                        </option>
                        <option value="Switched Off">
                          Switched Off
                        </option>
                        <option value="Out of Coverage">
                          Out of Coverage
                        </option>
                        <option value="Not Answered">
                          Not Answered
                        </option>
                        <option value="Prospect">
                          Prospect
                        </option>
                        <option value="Converted">
                          Converted
                        </option>
                      </select>
                    </th>

                    <th>
                      <input
                        type="text"
                        value={
                          remarkColumnFilter
                        }
                        onChange={(
                          event
                        ) =>
                          setRemarkColumnFilter(
                            event.target.value
                          )
                        }
                        placeholder="Filter remark"
                        style={
                          columnInputStyle
                        }
                      />
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {leadRows.length ===
                  0 ? (
                    <tr>
                      <td colSpan="5">
                        No records found.
                      </td>
                    </tr>
                  ) : (
                    leadRows.map(
                      (lead) => (
                        <tr
                          key={
                            lead.id
                          }
                        >
                          <td>
                            <button
                              type="button"
                              onClick={() =>
                                navigate(
                                  `/admin-lead/${lead.id}`
                                )
                              }
                              style={{
                                border:
                                  "none",
                                background:
                                  "transparent",
                                padding: 0,
                                color:
                                  "#0284c7",
                                fontWeight:
                                  "800",
                                cursor:
                                  "pointer",
                                textAlign:
                                  "left",
                              }}
                            >
                              {lead.customerName ||
                                lead.name ||
                                "Customer"}
                            </button>
                          </td>

                          <td>
                            {lead.phone ||
                              "-"}
                          </td>

                          <td>
                            {lead.assignedEmployeeName ||
                              "-"}
                          </td>

                          <td>
                            {lead.status ||
                              "Pending"}
                          </td>

                          <td>
                            {lead.lastRemark ||
                              "-"}
                          </td>
                        </tr>
                      )
                    )
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}

export default AdminReports;